import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Locale } from '@hub/contracts'
import { useLocale } from '@/i18n/locale'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Monitor, Moon, Sun } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ButtonGroup } from '@/components/ui/button-group'
import { useAuthState } from '@/lib/auth-state'
import { useTheme, type ThemePreference } from '@/lib/theme'
import { TTS_SPEEDS, formatTtsSpeed, useTtsSpeed } from '@/lib/tts-speed'
import { useTtsVoice, type TtsVoicePreference } from '@/lib/tts-voice'
import { usePushSubscription } from '@/lib/notifications/use-push-subscription'
import { orpc, toApiUrl } from '@/lib/orpc-client'
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
  dangerButtonClass,
  settingsInputClass,
} from './settings-layout'

const ALLOWED_AVATAR_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024

function fileToBase64Data(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const value = reader.result
      if (typeof value !== 'string') {
        reject(new Error('Unable to parse file'))
        return
      }

      const separatorIndex = value.indexOf(',')
      resolve(separatorIndex >= 0 ? value.slice(separatorIndex + 1) : value)
    }

    reader.onerror = () => {
      reject(new Error('Unable to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/** The profile card (avatar, name/phone, language) and the browser-state card. */
export function ProfileSettings() {
  const { me, status, refresh } = useAuthState()
  const { t } = useTranslation(['settings', 'common'])
  const { locale, setLocale } = useLocale()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveNotice, setSaveNotice] = useState<string | null>(null)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const [avatarNotice, setAvatarNotice] = useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!me) {
      return
    }

    setName(me.user.name ?? '')
    setPhone(me.user.phone ?? '')
  }, [me])

  const hasChanges = useMemo(() => {
    if (!me) {
      return false
    }
    return (me.user.name ?? '') !== name || (me.user.phone ?? '') !== phone
  }, [me, name, phone])

  const avatarUrl = me?.user.avatarUrl ? toApiUrl(me.user.avatarUrl) : null

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaveError(null)
    setSaveNotice(null)

    const trimmedName = name.trim()
    const trimmedPhone = phone.trim()

    if (!trimmedName && !trimmedPhone) {
      setSaveError(t('profile.validation'))
      return
    }

    setIsSaving(true)
    try {
      await orpc.me.updateProfile({
        name: trimmedName || undefined,
        phone: trimmedPhone || undefined,
      })
      await refresh()
      setSaveNotice(t('profile.saved'))
    } catch {
      setSaveError(t('profile.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleAvatarSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.currentTarget.value = ''

    if (!file) {
      return
    }

    setAvatarError(null)
    setAvatarNotice(null)

    if (!ALLOWED_AVATAR_MIME_TYPES.has(file.type)) {
      setAvatarError(t('profile.avatarInvalidType'))
      return
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      setAvatarError(t('profile.avatarTooLarge'))
      return
    }

    setIsUploadingAvatar(true)
    try {
      const base64Data = await fileToBase64Data(file)
      await orpc.me.uploadAvatar({
        fileName: file.name,
        mimeType: file.type,
        base64Data,
      })
      await refresh()
      setAvatarNotice(t('profile.avatarUpdated'))
    } catch {
      setAvatarError(t('profile.avatarUploadFailed'))
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const changeLanguage = (next: Locale) => {
    if (next === locale) return
    setLocale(next)
    // Persist on the profile so the choice follows the user between devices.
    void orpc.me
      .updateProfile({ locale: next })
      .then(() => refresh())
      .catch(() => {})
  }

  if (status === 'loading') {
    return (
      <SettingsPage>
        <p className="py-6 text-sm text-muted-foreground">{t('profile.loading')}</p>
      </SettingsPage>
    )
  }

  return (
    <SettingsPage>
      <SettingsSection>
        <div className="flex flex-wrap items-center gap-4">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={t('profile.avatarAlt')}
              className="h-14 w-14 shrink-0 rounded-full object-cover border border-border"
            />
          ) : (
            <div className="h-14 w-14 shrink-0 rounded-full border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">
              {t('profile.noImage')}
            </div>
          )}

          <p className="min-w-0 flex-1 text-xs text-muted-foreground">{t('profile.avatarHint')}</p>

          {/* Same pattern as Projekt › Utseende: a hidden input behind a translated button
              (the native file control is drawn in the browser's language). */}
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleAvatarSelected}
            disabled={isUploadingAvatar}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-action="upload-avatar"
            disabled={isUploadingAvatar}
            onClick={() => avatarInputRef.current?.click()}
          >
            {isUploadingAvatar ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {t('profile.uploading')}
              </>
            ) : (
              t('profile.uploadImage')
            )}
          </Button>
        </div>
        {avatarError && <p className="text-sm text-destructive">{avatarError}</p>}
        {avatarNotice && <p className="text-sm text-muted-foreground">{avatarNotice}</p>}
      </SettingsSection>

      <SettingsSection description={t('profile.description')}>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">{t('profile.name')}</span>
            <input
              type="text"
              className={settingsInputClass}
              placeholder={t('profile.namePlaceholder')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">{t('profile.phone')}</span>
            <input
              type="tel"
              className={settingsInputClass}
              placeholder={t('profile.phonePlaceholder')}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          {saveNotice && <p className="text-sm text-muted-foreground">{saveNotice}</p>}

          <div>
            <Button type="submit" disabled={isSaving || !hasChanges}>
              {isSaving ? t('profile.saving') : t('profile.save')}
            </Button>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection>
        <SettingsRow
          label={t('profile.language')}
          description={t('profile.languageHint')}
          htmlFor="settings-language"
        >
          <Select value={locale} onValueChange={(value) => changeLanguage(value as Locale)}>
            <SelectTrigger id="settings-language" className="w-40" data-testid="settings-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sv">{t('common:language.sv')}</SelectItem>
              <SelectItem value="en">{t('common:language.en')}</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>
        <DevicePreferenceRows />
      </SettingsSection>

      <BrowserStateSection />
    </SettingsPage>
  )
}

/**
 * Theme and read-aloud voice/speed. Theme is the same context the user menu's
 * Theme entry uses (`useTheme`); voice/speed live only here since the user
 * menu dropped them, and read the shared stores (`useTtsVoice`, `useTtsSpeed`).
 */
function DevicePreferenceRows() {
  const { t } = useTranslation(['settings', 'common'])
  const { preference: theme, setPreference: setTheme } = useTheme()
  const [voice, setVoice] = useTtsVoice()
  const [speed, setSpeed] = useTtsSpeed()

  const themes: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: t('common:userMenu.light'), icon: Sun },
    { value: 'dark', label: t('common:userMenu.dark'), icon: Moon },
    { value: 'system', label: t('common:userMenu.system'), icon: Monitor },
  ]
  const voices: { value: TtsVoicePreference; label: string }[] = [
    { value: 'female', label: t('common:voice.female') },
    { value: 'male', label: t('common:voice.male') },
  ]

  return (
    <>
      <SettingsRow label={t('profile.theme')} description={t('profile.themeHint')}>
        <ButtonGroup role="radiogroup" aria-label={t('profile.theme')} data-testid="settings-theme">
          {themes.map(({ value, label, icon: Icon }) => (
            <Button
              key={value}
              type="button"
              role="radio"
              size="sm"
              variant={theme === value ? 'default' : 'outline'}
              aria-checked={theme === value}
              data-value={value}
              onClick={() => setTheme(value)}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </SettingsRow>

      <SettingsRow label={t('profile.voice')} description={t('profile.voiceHint')}>
        <ButtonGroup role="radiogroup" aria-label={t('profile.voice')} data-testid="settings-voice">
          {voices.map(({ value, label }) => (
            <Button
              key={value}
              type="button"
              role="radio"
              size="sm"
              variant={voice === value ? 'default' : 'outline'}
              aria-checked={voice === value}
              data-value={value}
              onClick={() => setVoice(value)}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </SettingsRow>

      <SettingsRow label={t('profile.speed')} description={t('profile.speedHint')}>
        <ButtonGroup role="radiogroup" aria-label={t('profile.speed')} data-testid="settings-speed">
          {TTS_SPEEDS.map((value) => (
            <Button
              key={value}
              type="button"
              role="radio"
              size="sm"
              variant={speed === value ? 'default' : 'outline'}
              aria-checked={speed === value}
              data-value={value}
              onClick={() => setSpeed(value)}
            >
              {formatTtsSpeed(value)}
            </Button>
          ))}
        </ButtonGroup>
      </SettingsRow>
    </>
  )
}

export function NotificationSettings() {
  const { isSupported, isSubscribed, isLoading, error, subscribe, unsubscribe, sendTest } =
    usePushSubscription()
  const { t } = useTranslation(['settings', 'common'])
  const [testState, setTestState] = useState<
    'idle' | 'sending' | 'sent' | 'none' | 'not-configured' | 'failed'
  >('idle')

  const handleTest = async () => {
    setTestState('sending')
    try {
      const result = await sendTest()
      if (!result.configured) setTestState('not-configured')
      else if (result.subscriptions === 0) setTestState('none')
      else if (result.sent > 0) setTestState('sent')
      else setTestState('failed')
    } catch {
      setTestState('failed')
    }
  }

  const errorText =
    error === 'permission-denied'
      ? t('push.permissionDenied')
      : error === 'not-configured'
        ? t('push.notConfigured')
        : error === 'unsubscribe-failed'
          ? t('push.unsubscribeFailed')
          : error === 'subscribe-failed'
            ? t('push.subscribeFailed')
            : null

  const testText =
    testState === 'sent'
      ? t('push.testSent')
      : testState === 'none'
        ? t('push.testNoSubscriptions')
        : testState === 'not-configured'
          ? t('push.notConfigured')
          : testState === 'failed'
            ? t('push.testFailed')
            : null

  return (
    <SettingsPage>
      <SettingsSection title={t('push.title')} description={t('push.description')}>
        <SettingsRow label={t('push.webPush')} description={t('push.webPushBody')}>
          {!isSupported ? (
            <p className="text-sm text-muted-foreground italic">{t('push.unsupported')}</p>
          ) : isSubscribed ? (
            <>
              <span className="text-sm text-green-600 dark:text-green-400">
                {t('push.enabled')}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTest}
                disabled={isLoading || testState === 'sending'}
              >
                {testState === 'sending' ? t('push.testing') : t('push.test')}
              </Button>
              <Button variant="outline" size="sm" onClick={unsubscribe} disabled={isLoading}>
                {isLoading ? t('push.disabling') : t('push.disable')}
              </Button>
            </>
          ) : (
            <Button variant="default" size="sm" onClick={subscribe} disabled={isLoading}>
              {isLoading ? t('push.enabling') : t('push.enable')}
            </Button>
          )}
        </SettingsRow>

        {errorText ? <p className="text-sm text-destructive">{errorText}</p> : null}
        {isSubscribed && testText ? (
          <p
            className={
              testState === 'sent' ? 'text-sm text-muted-foreground' : 'text-sm text-destructive'
            }
          >
            {testText}
          </p>
        ) : null}
      </SettingsSection>
    </SettingsPage>
  )
}

/** PLAN-083: forget the logins HUB's shared browser keeps for this user. */
function BrowserStateSection() {
  const { t } = useTranslation('settings')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const clear = async () => {
    setBusy(true)
    setNotice(null)
    try {
      await orpc.me.clearBrowserState({})
      setNotice(t('browser.cleared'))
      setConfirming(false)
    } catch {
      setNotice(t('browser.clearFailed'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <SettingsSection
      tone="danger"
      title={t('browser.title')}
      description={t('browser.description')}
    >
      <div>
        <Button
          variant="outline"
          size="sm"
          className={dangerButtonClass}
          onClick={() => setConfirming(true)}
          disabled={busy}
        >
          {t('browser.clear')}
        </Button>
      </div>
      {notice && <p className="text-muted-foreground text-sm">{notice}</p>}
      <Dialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('browser.confirmTitle')}</DialogTitle>
            <DialogDescription>{t('browser.confirmDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
              {t('browser.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => void clear()} disabled={busy}>
              {busy ? t('browser.clearing') : t('browser.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  )
}
